// Teaching levels — the HOW of the 10-level progression. Each level is one
// teaching mechanic; the WHAT (which 5 words) comes from the selected
// word pack. The same pack can be played through all 10 levels.
//
// Course modes are reused across levels (the runner game stays the same;
// only the challenge type at each gate changes).

import type { ChallengeKind } from "@shared/types";

export interface TeachingLevel {
  /** 1..10. Used in URLs (`/play/:packId/L:level`). */
  index: number;
  title: string;
  /** Short hint shown in the picker. */
  subtitle: string;
  /** Which course shape the runner uses. */
  courseMode: "hit-parade" | "ice-crack" | "door-dash" | "rolling-tubes" | "circle-spinner" | "maze-rush" | "space-ship" | "supermarket-sweep" | "block-climb" | "river-boat" | "double-spinner" | "train-roof";
  /** Which challenge type appears at each gate. Some levels use a special
   *  flag rather than an existing ChallengeKind (see specialChallenge). */
  challengeKinds?: ChallengeKind[] | null;
  /** Levels that need a UI we don't have under ChallengeKind get this tag.
   *  ChallengeRouter consults it to render the new component. */
  specialChallenge?:
    | "missing-letter"   // word with one blanked letter, pick from 3
    | "spell-tiles"      // drag tiles to spell the whole word
    | "mixed";           // each question is a different random format
  /** Course length override (defaults to 260 for level 1-style runs). */
  courseLength?: number;
}

// Difficulty re-ordering pass (per playtest feedback):
//   • old Level 2 "See & Hear" (ice-crack) felt too hard early on, so it
//     moves to slot 10 as a late-game challenge.
//   • old Level 3 "Fill the Gap" (door-dash) becomes the new slot 2.
// Everything else shifts up by one to keep a 10-level progression.
// Per playtest feedback the spelling / sentence-gap / audio-to-text
// formats were too hard for the target age group. Every level now uses
// either `audio-to-image` (hear the word → pick the picture, text
// visible on the choice) or `image-to-audio` (see the picture → pick
// the spoken word). Levels alternate so kids practise both directions.
export const TEACHING_LEVELS: TeachingLevel[] = [
  {
    index: 1,
    title: "Listen & Look",
    subtitle: "Hear the word, pick the picture",
    courseMode: "hit-parade",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 2,
    title: "Look & Listen",
    subtitle: "See the picture, pick the spoken word",
    courseMode: "door-dash",
    challengeKinds: ["image-to-audio"],
  },
  {
    index: 3,
    title: "Hear & Read",
    subtitle: "Hear the word, pick the picture",
    courseMode: "rolling-tubes",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 4,
    title: "Spinner Run",
    subtitle: "See the picture, pick the word",
    courseMode: "circle-spinner",
    challengeKinds: ["image-to-audio"],
  },
  {
    index: 5,
    title: "Maze Rush",
    subtitle: "Hear the word, pick the picture",
    courseMode: "maze-rush",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 6,
    title: "Spaceship Word Run",
    subtitle: "See the picture, pick the word",
    courseMode: "space-ship",
    challengeKinds: ["image-to-audio"],
  },
  {
    index: 7,
    title: "Listen & Read",
    subtitle: "Hear the word, pick the picture",
    courseMode: "ice-crack",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 8,
    title: "River Boat Run",
    subtitle: "See the picture, pick the word",
    courseMode: "river-boat",
    challengeKinds: ["image-to-audio"],
  },
  {
    index: 9,
    title: "Block Climb Ball Run",
    subtitle: "Hear the word, pick the picture",
    courseMode: "block-climb",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 10,
    title: "Train Roof Dash",
    subtitle: "Race the rooftops — pick the picture",
    courseMode: "train-roof",
    challengeKinds: ["audio-to-image"],
  },
  {
    index: 11,
    title: "Double Spinner Survival",
    subtitle: "Last player standing — pick the word",
    courseMode: "double-spinner",
    challengeKinds: ["image-to-audio"],
  },
];

export function getTeachingLevel(index: number): TeachingLevel | undefined {
  return TEACHING_LEVELS.find((l) => l.index === index);
}
